import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { onValue } from 'firebase/database'
import { Calendar, Users, Flag, BarChart3, Plus, FileText, RefreshCw, Megaphone, ChevronRight, CheckCircle2, ArrowRight } from 'lucide-react'
import { tref } from '../lib/firebase.js'
import { watchTournaments } from '../lib/data.js'
import CLUB from '../config/club.js'

// Golf Elite HOME / DASHBOARD — clean full-width work page under the shared top
// nav (AdminShell → GeTopNav). EVENT-first. Everything is a hub. Real club data
// only: empty club → empty states + zero counts. No wave/glass here.
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const FLAG_COLORS = ['#e5484d', '#f0892a', '#22a452', '#2f6fe6']

function playersOf(ev) {
  if (Number.isFinite(ev.playerCount)) return ev.playerCount
  let n = 0
  const f = ev._flights || {}
  for (const fid in f) { const teams = f[fid].teams || {}; for (const tid in teams) { const pl = teams[tid].info?.players; n += Array.isArray(pl) ? pl.length : 1 } }
  if (n === 0) { const r = ev._regs || {}; for (const rid in r) { const pl = r[rid].players; n += Array.isArray(pl) ? pl.length : 1 } }
  return n
}

export default function GolfEliteHome() {
  const navigate = useNavigate()
  const [tournaments, setTournaments] = useState({})
  const [meta, setMeta] = useState(null)
  const [calRef, setCalRef] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() } })

  useEffect(() => { const off = watchTournaments(setTournaments); return () => off() }, [])
  useEffect(() => { const off = onValue(tref('meta'), (s) => setMeta(s.val()), () => setMeta(null)); return () => off() }, [])

  const todayStr = new Date().toISOString().slice(0, 10)
  const todayD = new Date()
  // Club profile (user-populated) — neutral placeholders until the club sets them.
  const clubBoxName = (meta?.name || '').trim() || 'Add your club name'
  const clubBoxLoc = [meta?.city, meta?.state].filter(Boolean).join(', ') || 'Add your location'

  const events = useMemo(() => Object.entries(tournaments)
    .filter(([, v]) => v && typeof v === 'object')
    .map(([id, v]) => ({ id, ...(v.info || {}), _flights: v.flights, _regs: v.registrations }))
    .filter((e) => !e.isTestStation && !e.isDemo)
  , [tournaments])

  const classify = (e) => {
    if (e.status === 'closed' || e.completed) return 'finished'
    if (e.date && e.date < todayStr) return 'finished'
    if (e.date && e.date === todayStr) return 'live'
    return 'upcoming'
  }
  const bucketed = useMemo(() => {
    const up = [], live = [], fin = []
    events.forEach((e) => { const s = classify(e); (s === 'live' ? live : s === 'finished' ? fin : up).push(e) })
    const asc = (a, b) => String(a.date || '').localeCompare(String(b.date || ''))
    const desc = (a, b) => String(b.date || '').localeCompare(String(a.date || ''))
    return { up: up.sort(asc), live: live.sort(asc), fin: fin.sort(desc) }
  }, [events, todayStr])

  const totalRounds = events.reduce((n, e) => n + playersOf(e), 0)
  const totalGolfers = new Set(events.flatMap((e) => {
    const names = []
    const f = e._flights || {}; for (const fid in f) { const teams = f[fid].teams || {}; for (const tid in teams) (teams[tid].info?.players || []).forEach((p) => names.push(String(p).toLowerCase())) }
    const r = e._regs || {}; for (const rid in r) (r[rid].players || []).forEach((p) => names.push(String(p).toLowerCase()))
    return names
  })).size

  const openEvent = (id) => navigate(`/admin/t/${id}`)
  const nf = (n) => Number(n || 0).toLocaleString('en-US')

  const create = [
    { Icon: Plus, color: 'blue', title: 'Fast Setup', sub: 'Create one round, one course event', to: '/admin/pbcc-4ball?create=1' },
    { Icon: FileText, color: 'green', title: 'New Event', sub: 'Build a new event from scratch', to: '/admin/pbcc-4ball?create=1' },
    { Icon: RefreshCw, color: 'orange', title: 'Use Previous Setup', sub: 'Start from a past event setup', to: '/admin/pbcc-4ball' },
    { Icon: Megaphone, color: 'purple', title: 'Advertising', sub: 'Promote your events and grow your club', to: '/admin/advertising' },
  ]
  const tiles = [
    { Icon: Calendar, color: 'blue', value: nf(events.length), label: 'Total Events', sub: 'This Year', to: '/admin/pbcc-4ball' },
    { Icon: Users, color: 'navy', value: nf(totalGolfers), label: 'Total Golfers', sub: 'Registered', to: '/admin/gg/members' },
    { Icon: Flag, color: 'green', value: nf(bucketed.live.length), label: 'Active Events', sub: 'In Progress', to: '/admin/pbcc-4ball' },
    { Icon: BarChart3, color: 'navy', value: nf(totalRounds), label: 'Total Rounds', sub: 'This Year', to: '/admin/order-of-merit' },
  ]

  // Calendar
  const eventDays = useMemo(() => {
    const map = {}
    events.forEach((e) => { if (!e.date) return; const d = new Date(e.date + 'T00:00:00'); if (d.getFullYear() === calRef.y && d.getMonth() === calRef.m) map[d.getDate()] = e.id })
    return map
  }, [events, calRef])
  const grid = useMemo(() => {
    const first = new Date(calRef.y, calRef.m, 1).getDay()
    const days = new Date(calRef.y, calRef.m + 1, 0).getDate()
    const cells = []
    for (let i = 0; i < first; i++) cells.push(null)
    for (let d = 1; d <= days; d++) cells.push(d)
    return cells
  }, [calRef])
  const stepMonth = (delta) => setCalRef((r) => { const m = r.m + delta; return { y: r.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 } })
  const isToday = (d) => d && todayD.getFullYear() === calRef.y && todayD.getMonth() === calRef.m && todayD.getDate() === d

  return (
    <div className="geh2">
      <Styles />
      <div className="geh2-head">
        <div className="geh2-head-left">
          <h1 className="geh2-club">Dashboard</h1>
          <p className="geh2-sub">Let's get started. Create your first event, add your golfers, or explore the tools below.</p>
        </div>
        <button type="button" className="geh2-clubbox" onClick={() => navigate('/admin/gg/settings')}>
          <span className="geh2-clubbox-ic"><Flag size={22} strokeWidth={2.2} color="#1f5fd0" /></span>
          <span className="geh2-clubbox-text">
            <span className="geh2-clubbox-name">{clubBoxName}</span>
            <span className="geh2-clubbox-loc">{clubBoxLoc}</span>
          </span>
          <ChevronRight size={20} className="geh2-clubbox-chev" />
        </button>
      </div>

      <div className="geh2-create">
        {create.map((c) => (
          <button key={c.title} type="button" className="geh2-ccard" onClick={() => navigate(c.to)}>
            <span className="geh2-cic" data-c={c.color}><c.Icon size={22} strokeWidth={2.4} /></span>
            <span className="geh2-ctext"><span className="geh2-ctitle">{c.title}</span><span className="geh2-csub">{c.sub}</span></span>
            <ChevronRight size={20} className="geh2-cchev" />
          </button>
        ))}
      </div>

      <div className="geh2-stats">
        {tiles.map((t) => (
          <button key={t.label} type="button" className="geh2-stat" onClick={() => navigate(t.to)}>
            <span className="geh2-stat-ic" data-c={t.color}><t.Icon size={24} strokeWidth={2} /></span>
            <span className="geh2-stat-body">
              <span className="geh2-stat-val">{t.value}</span>
              <span className="geh2-stat-label">{t.label}</span>
              <span className="geh2-stat-sub">{t.sub}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="geh2-grid">
        <EventCard title="Upcoming Events" icon={<Calendar size={20} strokeWidth={2.2} color="#13294A" />} rows={bucketed.up} kind="upcoming"
          emptyIcon={<Calendar size={28} strokeWidth={2} color="#7e8ca3" />} emptyTitle="No upcoming events yet" emptySub="Create your first event to get started."
          emptyBtn={{ label: 'New Event', onClick: () => navigate('/admin/pbcc-4ball?create=1') }}
          onOpen={openEvent} onViewAll={() => navigate('/admin/pbcc-4ball')} />
        <EventCard title="Recently Finished" icon={<CheckCircle2 size={20} strokeWidth={2.2} color="#13294A" />} rows={bucketed.fin} kind="finished"
          emptyIcon={<Flag size={28} strokeWidth={2} color="#7e8ca3" />} emptyTitle="No completed events yet" emptySub="Your finished events will appear here."
          onOpen={openEvent} onViewAll={() => navigate('/admin/order-of-merit')} />
        <aside className="geh2-right">
          <section className="geh2-card geh2-cal">
            <div className="geh2-card-head"><Calendar size={20} strokeWidth={2.2} color="#13294A" /><h3>Calendar</h3></div>
            <div className="geh2-cal-nav">
              <button type="button" onClick={() => stepMonth(-1)} aria-label="Previous month">‹</button>
              <span>{MONTH_LONG[calRef.m]} {calRef.y}</span>
              <button type="button" onClick={() => stepMonth(1)} aria-label="Next month">›</button>
            </div>
            <div className="geh2-cal-dow">{DOW.map((d) => <span key={d}>{d}</span>)}</div>
            <div className="geh2-cal-grid">
              {grid.map((d, i) => {
                if (!d) return <span key={i} className="geh2-cal-cell empty" />
                const evId = eventDays[d]
                const cls = `geh2-cal-cell${isToday(d) ? ' today' : ''}${evId ? ' hasev' : ''}`
                return evId
                  ? <button key={i} type="button" className={cls} onClick={() => openEvent(evId)}>{d}</button>
                  : <span key={i} className={cls}>{d}</span>
              })}
            </div>
            <button type="button" className="geh2-cal-full" onClick={() => navigate('/admin/pbcc-4ball')}>View Full Calendar <ArrowRight size={15} /></button>
          </section>
        </aside>
      </div>
    </div>
  )
}

function StatusBadge({ kind }) {
  const map = { upcoming: ['Upcoming', 'up'], live: ['In Progress', 'live'], finished: ['Completed', 'fin'] }
  const [label, c] = map[kind] || map.upcoming
  return <span className={`geh2-badge ${c}`}>{label}</span>
}

function EventCard({ title, icon, rows, kind, emptyIcon, emptyTitle, emptySub, emptyBtn, onOpen, onViewAll }) {
  const d = (iso) => { if (!iso) return { mon: '', day: '' }; const x = new Date(iso + 'T00:00:00'); return { mon: MONTHS[x.getMonth()], day: x.getDate() } }
  return (
    <section className="geh2-card geh2-evcard">
      <div className="geh2-ev-head">
        <div className="geh2-ev-title">{icon}<h2>{title}</h2></div>
        <button type="button" className="geh2-viewall" onClick={onViewAll}>View All <ArrowRight size={15} /></button>
      </div>
      {rows.length === 0 ? (
        <div className="geh2-empty2">
          <span className="geh2-empty-ic">{emptyIcon}</span>
          <div className="geh2-empty-title">{emptyTitle}</div>
          <div className="geh2-empty-sub">{emptySub}</div>
          {emptyBtn ? <button type="button" className="geh2-empty-btn" onClick={emptyBtn.onClick}><Plus size={17} strokeWidth={2.6} /> {emptyBtn.label}</button> : null}
        </div>
      ) : (
        <div className="geh2-rows">
          {rows.map((e, i) => {
            const dd = d(e.date)
            return (
              <div key={e.id} className="geh2-row" role="button" tabIndex={0} onClick={() => onOpen(e.id)} onKeyDown={(ev) => { if (ev.key === 'Enter') onOpen(e.id) }}>
                <span className="geh2-date"><b>{dd.mon}</b><em>{dd.day}</em></span>
                <span className="geh2-flag"><Flag size={20} strokeWidth={2} color={FLAG_COLORS[i % FLAG_COLORS.length]} fill={FLAG_COLORS[i % FLAG_COLORS.length]} /></span>
                <span className="geh2-rowmid">
                  <span className="geh2-rowname">{e.name}</span>
                  <span className="geh2-rowmeta">{(e.course || '—')} · {(e.format || '—')}</span>
                  <span className="geh2-rowplayers"><Users size={13} strokeWidth={2.2} /> {playersOf(e) || 0} Players</span>
                </span>
                <span className="geh2-rowstatus"><StatusBadge kind={kind} /></span>
                <ChevronRight size={18} className="geh2-rowchev" />
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

// Minimal clean Advertising page (the "Advertising" nav + card destination).
export function GolfEliteAdvertising() {
  return (
    <div className="geh2">
      <Styles />
      <div className="geh2-head">
        <div className="geh2-eyebrow">Advertising</div>
        <h1 className="geh2-club">Promote your events</h1>
      </div>
      <section className="geh2-card" style={{ padding: 24, maxWidth: 520 }}>
        <div className="geh2-card-head" style={{ padding: 0, marginBottom: 12 }}><Megaphone size={20} strokeWidth={2.2} color="#13294A" /><h3>Campaigns</h3></div>
        <button type="button" className="geh2-adbtn" disabled>Coming soon</button>
      </section>
    </div>
  )
}

function Styles() {
  return (
    <style>{`
      /* Full-height flex column: fills the viewport below the 104px top nav and
         shares vertical space between the rows. No fixed heights lock it — the
         bottom row flexes to fill whatever remains; inner lists scroll. */
      .geh2 { height: calc(100dvh - 105px); display: flex; flex-direction: column; gap: 16px; padding: 18px 28px; overflow-y: auto; font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1e2b40; }
      .geh2 * { box-sizing: border-box; }
      .geh2-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; flex: 0 0 auto; }
      .geh2-head-left { min-width: 0; }
      .geh2-eyebrow { font-size: 11px; font-weight: 800; letter-spacing: .15em; color: #8491a7; text-transform: uppercase; }
      .geh2-club { margin: 0; font-family: Georgia, 'Times New Roman', serif; font-size: 44px; font-weight: 700; color: #122a4e; line-height: 1.04; letter-spacing: -0.01em; }
      .geh2-sub { margin: 8px 0 0; font-size: 15px; color: #56657e; max-width: 640px; }
      .geh2-clubbox { flex-shrink: 0; display: flex; align-items: center; gap: 13px; padding: 14px 18px; background: #fff; border: 1px solid #e7ecf3; border-radius: 14px; box-shadow: 0 2px 8px rgba(20,40,80,0.05); cursor: pointer; text-align: left; min-width: 280px; }
      .geh2-clubbox:hover { box-shadow: 0 10px 26px rgba(20,40,80,0.12); }
      .geh2-clubbox-ic { width: 48px; height: 48px; flex: 0 0 48px; border-radius: 12px; background: #e8f1fd; display: grid; place-items: center; }
      .geh2-clubbox-text { display: flex; flex-direction: column; flex: 1; min-width: 0; }
      .geh2-clubbox-name { font-size: 16px; font-weight: 800; color: #13294a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .geh2-clubbox-loc { font-size: 13px; color: #7a869c; margin-top: 1px; }
      .geh2-clubbox-chev { color: #b7c0d0; flex-shrink: 0; }

      .geh2-create { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; flex: 0 0 auto; }
      @media (max-width: 1050px) { .geh2-create { grid-template-columns: repeat(2, 1fr); } }
      .geh2-ccard { display: flex; align-items: center; gap: 15px; padding: 21px 22px; background: #fff; border: 1px solid #e7ecf3; border-radius: 14px; cursor: pointer; text-align: left; box-shadow: 0 2px 8px rgba(20,40,80,0.05); transition: box-shadow .15s ease, transform .15s ease; }
      .geh2-ccard:hover { box-shadow: 0 10px 26px rgba(20,40,80,0.12); transform: translateY(-1px); }
      .geh2-cic { width: 52px; height: 52px; flex: 0 0 52px; border-radius: 50%; display: grid; place-items: center; }
      .geh2-ctext { flex: 1; display: flex; flex-direction: column; min-width: 0; }
      .geh2-ctitle { font-size: 16px; font-weight: 800; color: #13294a; }
      .geh2-csub { font-size: 12.5px; color: #7a869c; margin-top: 1px; }
      .geh2-cchev { color: #c2cbd9; flex-shrink: 0; }
      .geh2-cic[data-c="blue"] { background: #e8f1fd; color: #1f6fe0; }
      .geh2-cic[data-c="green"] { background: #e6f6ec; color: #1a9e4b; }
      .geh2-cic[data-c="orange"] { background: #fdeede; color: #ed8a2a; }
      .geh2-cic[data-c="purple"] { background: #f1e9fc; color: #8a4fe0; }

      .geh2-stats { display: grid; grid-template-columns: repeat(4, 1fr); background: #fff; border: 1px solid #e7ecf3; border-radius: 16px; box-shadow: 0 2px 8px rgba(20,40,80,0.05); overflow: hidden; flex: 0 0 auto; }
      @media (max-width: 1050px) { .geh2-stats { grid-template-columns: repeat(2, 1fr); } }
      .geh2-stat { display: flex; align-items: center; gap: 16px; padding: 22px 26px; border: 0; border-right: 1px solid #eef1f6; background: transparent; cursor: pointer; text-align: left; }
      .geh2-stat:last-child { border-right: 0; }
      .geh2-stat:hover { background: #f8fbff; }
      .geh2-stat-ic { width: 54px; height: 54px; flex: 0 0 54px; border-radius: 50%; display: grid; place-items: center; }
      .geh2-stat-ic[data-c="blue"] { background: #e8f1fd; color: #1f5fd0; }
      .geh2-stat-ic[data-c="navy"] { background: #e9edf5; color: #1c3157; }
      .geh2-stat-ic[data-c="green"] { background: #e6f6ec; color: #1a9e4b; }
      .geh2-stat-body { display: flex; flex-direction: column; }
      .geh2-stat-val { font-size: 28px; font-weight: 800; color: #122a4e; line-height: 1.05; }
      .geh2-stat-label { font-size: 13.5px; font-weight: 700; color: #35435c; }
      .geh2-stat-sub { font-size: 12px; color: #8a97ac; }

      .geh2-grid { display: grid; grid-template-columns: repeat(3, 1fr); grid-template-rows: minmax(0, 1fr); gap: 22px; align-items: stretch; flex: 1 1 0; min-height: 0; }
      @media (max-width: 1200px) { .geh2-grid { grid-template-columns: 1fr 1fr; } .geh2-right { grid-column: 1 / -1; } }
      @media (max-width: 760px) { .geh2-grid { grid-template-columns: 1fr; } }

      .geh2-card { background: #fff; border: 1px solid #e7ecf3; border-radius: 15px; box-shadow: 0 2px 8px rgba(20,40,80,0.05); }
      .geh2-card-head { display: flex; align-items: center; gap: 9px; padding: 16px 18px 0; }
      .geh2-card-head h3 { margin: 0; font-size: 17px; font-weight: 800; color: #12294a; }
      .geh2-evcard { padding: 16px 18px 18px; display: flex; flex-direction: column; min-height: 0; }
      .geh2-ev-head { flex: 0 0 auto; }
      .geh2-ev-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
      .geh2-ev-title { display: flex; align-items: center; gap: 10px; }
      .geh2-ev-title h2 { margin: 0; font-size: 19px; font-weight: 800; color: #12294a; }
      .geh2-livedot { width: 13px; height: 13px; border-radius: 50%; background: #17b24f; box-shadow: 0 0 0 4px rgba(23,178,79,0.18); display: inline-block; }
      .geh2-viewall { border: 0; background: transparent; color: #2e6fe6; font-size: 13.5px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; }
      .geh2-viewall:hover { text-decoration: underline; }
      .geh2-empty2 { flex: 1 1 0; min-height: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 24px 20px; gap: 7px; }
      .geh2-empty-ic { width: 72px; height: 72px; border-radius: 50%; background: #eef2f8; display: grid; place-items: center; margin-bottom: 8px; }
      .geh2-empty-title { font-size: 18px; font-weight: 800; color: #122a4e; }
      .geh2-empty-sub { font-size: 14px; color: #7a869c; }
      .geh2-empty-btn { margin-top: 14px; display: inline-flex; align-items: center; gap: 8px; height: 46px; padding: 0 24px; border: 0; border-radius: 11px; background: #1f66e0; color: #fff; font-size: 14.5px; font-weight: 700; cursor: pointer; box-shadow: 0 8px 20px rgba(31,102,224,0.28); }
      .geh2-empty-btn:hover { background: #1557c8; }

      .geh2-rows { width: 100%; flex: 1 1 0; min-height: 0; overflow-y: auto; }
      .geh2-row { display: grid; grid-template-columns: 58px 30px minmax(0,1fr) auto 22px; gap: 13px; align-items: center; padding: 13px 4px; border-top: 1px solid #f1f4f8; cursor: pointer; transition: background .12s ease; }
      .geh2-row:hover { background: #f7faff; }
      .geh2-date { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 54px; height: 50px; border-radius: 10px; background: #f2f5fa; border: 1px solid #e8edf4; }
      .geh2-date b { font-size: 10.5px; font-weight: 800; color: #8794aa; letter-spacing: .04em; }
      .geh2-date em { font-style: normal; font-size: 19px; font-weight: 800; color: #12294a; line-height: 1; margin-top: 1px; }
      .geh2-flag { display: grid; place-items: center; }
      .geh2-rowmid { display: flex; flex-direction: column; min-width: 0; }
      .geh2-rowname { font-size: 15px; font-weight: 800; color: #16233c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .geh2-rowmeta { font-size: 13px; color: #7a869c; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .geh2-rowplayers { display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; color: #8793a8; margin-top: 3px; }
      .geh2-badge { display: inline-block; padding: 5px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; white-space: nowrap; }
      .geh2-badge.up { background: #e8f1fd; color: #1f66d6; }
      .geh2-badge.live { background: #e3f6ea; color: #138a41; }
      .geh2-badge.fin { background: #eef1f6; color: #5a6880; }
      .geh2-rowchev { color: #b7c0d0; }

      .geh2-cal { padding-bottom: 16px; display: flex; flex-direction: column; min-height: 0; overflow-y: auto; }
      .geh2-cal-nav { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px 6px; }
      .geh2-cal-nav button { border: 0; background: transparent; cursor: pointer; font-size: 20px; color: #6b7a93; width: 30px; height: 30px; border-radius: 8px; }
      .geh2-cal-nav button:hover { background: #eef2f8; color: #12294a; }
      .geh2-cal-nav span { font-size: 15px; font-weight: 800; color: #12294a; }
      .geh2-cal-dow { display: grid; grid-template-columns: repeat(7, 1fr); padding: 2px 14px 0; }
      .geh2-cal-dow span { text-align: center; font-size: 11px; font-weight: 700; color: #9aa6ba; padding: 4px 0; }
      .geh2-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); padding: 2px 14px 0; gap: 2px; }
      .geh2-cal-cell { aspect-ratio: 1; display: grid; place-items: center; font-size: 13px; color: #44536b; border: 0; background: transparent; border-radius: 50%; }
      .geh2-cal-cell.empty { visibility: hidden; }
      .geh2-cal-cell.hasev { cursor: pointer; background: #eaf2fc; color: #1f5fd0; font-weight: 700; }
      .geh2-cal-cell.hasev:hover { background: #d9e8fb; }
      .geh2-cal-cell.today { background: #2e6fe6; color: #fff; font-weight: 800; }
      .geh2-cal-full { width: calc(100% - 28px); margin: 14px 14px 0; margin-top: auto; height: 44px; flex: 0 0 44px; border: 0; border-radius: 10px; background: #eef4fd; color: #2e6fe6; font-size: 14px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 6px; }
      .geh2-cal-full:hover { background: #e2edfb; }

      .geh2-adbtn { height: 44px; padding: 0 26px; border: 1px solid #13294A; background: #13294A; color: #fff; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; opacity: .6; }
    `}</style>
  )
}
